"use client";

import React, { createContext, useContext, useEffect, useState } from "react";
import useAudioPlayer from "@/hooks/music-players/use-audio-player";
import { usePathname } from "next/navigation";

interface MusicContextType {
  currentTrack: string | null;
  isPlaying: boolean;
  playTrack: (track: string, index: number, playlist: string[]) => void;
  pauseTrack: () => void;
  resumeTrack: () => void;
  nextTrack: () => void;
  setPlaylist: (tracks: string[]) => void;
  musicPlayer: React.RefObject<HTMLAudioElement>;
  progressRef: React.RefObject<HTMLInputElement>;
  changeRange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  calculateTime: (secs: number) => string;
  onDurationChanged: (
    event: React.SyntheticEvent<HTMLMediaElement, Event>,
  ) => void;
  onTimeUpdate: () => void;
  currentIndex: number;
  currentTime: number;
  isMuted: boolean;
  toggleMute: () => void;
  volume: number;
  changeVolume: (volumeLevel: number) => void;
  closePlayer: () => void;
  isTrackPlaying: (track: string) => boolean;
  playPause: (track: string, onPlayCallback: (track: string) => void) => void;
  volumeProgressRef: React.RefObject<HTMLInputElement>;
}

const MusicContext = createContext<MusicContextType | null>(null);

export const MusicProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [playlist, setPlaylist] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState<number>(0);
  const [isPlaying, setIsPlaying] = useState(false);
  const pathname = usePathname();

  const {
    musicPlayer,
    progressRef,
    changeRange,
    calculateTime,
    onDurationChanged,
    onTimeUpdate,
    currentTime,
    isMuted,
    toggleMute,
    volume,
    changeVolume,
    volumeProgressRef,
  } = useAudioPlayer();

  const currentTrack = playlist[currentIndex] || null;

  const playTrack = async (
    track: string,
    index: number,
    playlist: string[],
  ) => {
    if (track === currentTrack) {
      resumeTrack();
    } else {
      setPlaylist(playlist);
      setCurrentIndex(index);
      setIsPlaying(true);
      if (musicPlayer.current) {
        musicPlayer.current.src = track;
        try {
          await musicPlayer.current.play();
        } catch (error: any) {
          if (error.name !== "AbortError") {
            console.error("Error playing the audio:", error);
          }
        }
      } else {
        musicPlayer.current = new Audio(track);
        try {
          await musicPlayer.current.play();
        } catch (error: any) {
          if (error.name !== "AbortError") {
            console.error("Error playing the audio:", error);
          }
        }
      }

      musicPlayer.current.onended = () => {
        setIsPlaying(false);
        nextTrack();
      };
    }
  };

  const pauseTrack = () => {
    setIsPlaying(false);
    if (musicPlayer.current) {
      musicPlayer.current.pause();
    }
  };

  const resumeTrack = () => {
    setIsPlaying(true);
    if (musicPlayer.current) {
      musicPlayer.current
        .play()
        .catch((error) => console.error("Error resuming the audio:", error));
    }
  };

  const nextTrack = () => {
    if (currentIndex < playlist.length - 1) {
      setCurrentIndex(currentIndex + 1);
      setIsPlaying(true);
    }
  };

  const closePlayer = () => {
    setPlaylist([]);
    setCurrentIndex(0);
    setIsPlaying(false);
  };

  const isTrackPlaying = (track: string) => {
    return isPlaying && currentTrack == track;
  };

  const playPause = (
    track: string,
    onPlayCallback: (track: string) => void,
  ) => {
    if (isTrackPlaying(track)) {
      pauseTrack();
      return;
    }

    onPlayCallback(track);
  };

  useEffect(() => {
    if (musicPlayer.current) {
      musicPlayer.current.pause();
      musicPlayer.current.src = "";
      setIsPlaying(false);
    }
  }, [pathname, musicPlayer]);

  return (
    <MusicContext.Provider
      value={{
        currentTrack,
        isPlaying,
        playTrack,
        pauseTrack,
        resumeTrack,
        nextTrack,
        setPlaylist,
        musicPlayer,
        progressRef,
        changeRange,
        calculateTime,
        onDurationChanged,
        onTimeUpdate,
        currentIndex,
        currentTime,
        isMuted,
        toggleMute,
        volume,
        changeVolume,
        closePlayer,
        isTrackPlaying,
        playPause,
        volumeProgressRef,
      }}
    >
      {children}
    </MusicContext.Provider>
  );
};

export const useMusicPlayer = () => {
  const musicContext = useContext(MusicContext);
  if (!musicContext) {
    throw new Error("useMusicPlayer must be used within a MusicProvider");
  }
  return musicContext;
};
